import React, { useContext, useMemo, useState } from 'react';
import { IProject, Project, CustomField, EntityValidations, ISpecificEntityFormProps, IUser, Contributor, ProjectDep, SaveStatus, IPPAppProjectFinancial, PPAppProjectFinancial, ICustomerFinancialYear } from '../../types';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrDropdown } from '../cr/CrDropdown';
import { CrTextField } from '../cr/CrTextField';
import { EntityForm } from '../EntityForm';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';
import { ReportingCycleService } from '../../services/ReportingCycleService';
import { CrCheckbox } from '../cr/CrCheckbox';
import { DataContext } from '../DataContext';
import { OrbUserContext } from '../OrbUserContext';
import { IntegrationContext } from '../IntegrationContext';
import { CrLabel } from '../cr/CrLabel';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';
import { CrCustomFields } from '../cr/CrCustomFields';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { CrCommandBar } from '../cr/CrCommandBar';
import useMobileDetector from '../useMobileDetector';
import EvidenceList from './EV/EvidenceList';

export class ProjectValidations extends EntityValidations {
    //public DirectorateID: string = null;
    public ParentType: string = null;
    public PortfolioID: string = null;
    public ProgrammeID: string = null;
    public StartDate: string = null;
    public EndDate: string = null;
    public EntityStatusID: string = null;
    public ReportingCycle: string = null;
    public CorporateProjectID: string = null;
    public OwnerL1: string = null;
    public OwnerL2: string = null;
    public OwnerL3: string = null;
    public ManagedByL2: string = null;
    public ManagedByL3: string = null;
}

// --- Project Financials: financial year helpers ------------------------------------
// FY start month/day now come from the customer's FYStartDate/FYEndDate (props), not hardcoded.

// Given a date and the FY start month/day, return the calendar year in which its financial year starts.
const getFYStartYear = (date: Date, fyStartMonth: number, fyStartDay: number): number => {
    const month = date.getMonth();
    const day = date.getDate();
    if (month > fyStartMonth || (month === fyStartMonth && day >= fyStartDay)) {
        return date.getFullYear();
    }
    return date.getFullYear() - 1;
};

const getFYLabel = (fyStartYear: number): string => {
    const startYY = (fyStartYear % 100).toString().padStart(2, '0');
    const endYY = ((fyStartYear + 1) % 100).toString().padStart(2, '0');
    return `FY ${startYY}/${endYY}`;
};

// Returns one FY label for every financial year the project's Start Date -> End Date range touches.
const getFYLabelsForRange = (startDate: Date, endDate: Date, fyStartMonth: number, fyStartDay: number): string[] => {
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return [];
    }
    const startFYYear = getFYStartYear(startDate, fyStartMonth, fyStartDay);
    const endFYYear = getFYStartYear(endDate, fyStartMonth, fyStartDay);
    const labels: string[] = [];
    for (let y = startFYYear; y <= endFYYear; y++) {
        labels.push(getFYLabel(y));
    }
    return labels;
};

// Chunks FY labels into rows of 2 (last row may have only 1, with the 2nd half left empty)
const chunkIntoPairs = (labels: string[]): string[][] => {
    const rows: string[][] = [];
    for (let i = 0; i < labels.length; i += 2) {
        rows.push(labels.slice(i, i + 2));
    }
    return rows;
};
// -------------------------------------------------------------------------------------
// -------------------------------------------------------------------------------------

export interface IProjectFormProps extends ISpecificEntityFormProps {
    customerFinancialYear?: ICustomerFinancialYear;
}

export const ProjectForm = (props: IProjectFormProps): React.ReactElement => {
    const { dataSourceName, disableProjectManagement } = useContext(IntegrationContext);
    const { userPermissions, orbConfig, userContext } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const { dataServices: { customFieldService, contributorService, projectService, projectDepService, ppAppProjectFinancialService }, lookupData,
        loadLookupData: { portfolios, programmes, projects, projectStages, projectPhases, zCoreLocations, customFieldDfns, groups, directorates, orgLevel3s, entityStatuses, userProjects, users: { all: allUsers } } } = useContext(DataContext);
    const orbOnlyProjectFlag = 'Local';
    const isMobile = useMobileDetector();
    const [evidence_ListFilterText, setEvidence_ListFilterText] = useState<string>('');

    // Derive FY start/end dates from the customer's financial year settings passed in via props.
    // Falls back to a plain calendar year if not yet loaded, so the form doesn't blow up while
    // PerformanceReportingAdmin's fetch is still in flight.
    const currentFYStart: Date = props.customerFinancialYear?.fyStartDate
        ? DateService.removeTimezoneOffset(props.customerFinancialYear.fyStartDate)
        : new Date(new Date().getFullYear(), 0, 1);
    const currentFYEnd: Date = props.customerFinancialYear?.fyEndDate
        ? DateService.removeTimezoneOffset(props.customerFinancialYear.fyEndDate)
        : new Date(new Date().getFullYear(), 11, 31);
    const fyStartMonth = currentFYStart.getMonth();
    const fyStartDay = currentFYStart.getDate();

    useMemo(() => portfolios(), [portfolios]);
    useMemo(() => programmes(), [programmes]);
    useMemo(() => projects(), [projects]);
    useMemo(() => projectStages(), [projectStages]);
    useMemo(() => projectPhases(), [projectPhases]);
    useMemo(() => zCoreLocations(), [zCoreLocations]);
    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => groups(), [groups]);
    useMemo(() => directorates(), [directorates]);
    useMemo(() => orgLevel3s(), [orgLevel3s]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    //useMemo(() => projects(), [projects]);
    useMemo(() => userProjects(), [userProjects]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = async (project: IProject): Promise<ProjectValidations> => {
        const errors = new ProjectValidations();

        if (project.Title === null || project.Title === '') {
            errors.Title = 'Project name is required..';
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (project.ParentTypeID === null) {
            errors.ParentType = `Parent Type is required`;
            errors.Valid = false;
        }
        else {
            errors.ParentType = null;
        }

        if (project.ParentTypeID === 1 && (project.PortfolioID === null || isNaN(project.PortfolioID))) {
            errors.PortfolioID = `Parent Portfolio is required`;
            errors.Valid = false;
        }
        else {
            errors.PortfolioID = null;
        }

        if (project.ParentTypeID === 2 && (project.ProgrammeID === null || isNaN(project.ProgrammeID))) {
            errors.ProgrammeID = `Parent Programme is required`;
            errors.Valid = false;
        }
        else {
            errors.ProgrammeID = null;
        }

        if (project.OwnerTypeID === 1 && project.OwnerL1ID === null) {
            errors.OwnerL1 = `Owner ${orbConfig.OrgL1Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.OwnerL1 = null;
        }
        if (project.OwnerTypeID === 2 && project.OwnerL2ID === null) {
            errors.OwnerL2 = `Owner ${orbConfig.OrgL2Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.OwnerL2 = null;
        }
        if (project.OwnerTypeID === 3 && project.OwnerL3ID === null) {
            errors.OwnerL3 = `Owner ${orbConfig.OrgL3Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.OwnerL3 = null;
        }
        if (project.ManagedByTypeID === 2 && project.ManagedByL2ID === null) {
            errors.ManagedByL2 = `Managed By ${orbConfig.OrgL2Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.ManagedByL2 = null;
        }
        if (project.ManagedByTypeID === 3 && project.ManagedByL3ID === null) {
            errors.ManagedByL3 = `Managed By ${orbConfig.OrgL3Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.ManagedByL3 = null;
        }

        // Start Date required
        if (!project.StartDate) {
            errors.StartDate = 'Start Date is required';
            errors.Valid = false;
        } else {
            errors.StartDate = null;
        }

        // Target End Date required
        if (!project.EndDate) {
            errors.EndDate = 'End Date is required';
            errors.Valid = false;
        } else {
            errors.EndDate = null;
        }

        // End date cannot be earlier than start date
        if (
            project.StartDate &&
            project.EndDate &&
            project.EndDate < project.StartDate
        ) {
            errors.EndDate = 'End date cannot be earlier than start date';
            errors.Valid = false;
        }

        if (!ReportingCycleService.reportingCycleIsValid(project)) {
            errors.ReportingCycle = 'Please select all values for the reporting cycle';
            errors.Valid = false;
        }
        else {
            errors.ReportingCycle = null;
        }

        if (project.CorporateProjectID && project.CorporateProjectID !== orbOnlyProjectFlag) {
            const existingProjectsWithCorporateId = await projectService.readProjectByCorporateId(project.CorporateProjectID);
            if (existingProjectsWithCorporateId.length > 1
                || (existingProjectsWithCorporateId.length === 1 && existingProjectsWithCorporateId[0].ID !== project.ID)) {
                errors.CorporateProjectID = 'An existing project has this identifier';
                errors.Valid = false;
            } else {
                errors.CorporateProjectID = null;
            }
        }

        return Promise.resolve(errors);
    };

    const projectUsers = (project: IProject): IUser[] => {
        if (project.ID && lookupData.Users && lookupData.Users.All) {
            return lookupData.Users.All.filter(u => LookupService.userIsInProject(lookupData, u.ID, project.ID)
                || LookupService.userIsContributor(project, u.ID)
                || u.ID === project.SeniorResponsibleOwnerUserID
                || u.ID === project.ReportApproverUserID
                || u.ID === project.ProjectManagerUserID
                || u.ID === project.ReportingLeadUserID
            );
        }
        return [];
    };

    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'All Delivery Apps - Projects',
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    const filteredProjectsForDepList = props.entityId > 0
        ? lookupData.Projects.filter(p => p.ID !== props.entityId)
        : lookupData.Projects;

    const handleEvidence_ChangeFilterText = (value: string): void => {
        setEvidence_ListFilterText(value);
    }

    return (
        <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: '100%' }}>
            <EntityForm<IProject, ProjectValidations>
                {...props}
                entityName="Project"
                mode='page'
                renderFormFields={(changeHandlers, formState, saveEntity) => {
                    const { changeTextField, changeDropdown, changeReportingCycle, changeUserPicker, changeMultiUserPicker,
                        changeDatePicker, changeMultiDropdown, changeMultiDropdownWithText, changeCheckbox, changeStatusDropdown, changeJson } = changeHandlers;
                    const { FormData: project, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;
                    const disableEdit = project.CorporateProjectID !== orbOnlyProjectFlag && disableProjectManagement;
                    const externalDataSource = project.DataSource && project.DataSource.toLowerCase() === 'verto' ? true : false;
                    const users = lookupData.Users && lookupData.Users.All;
                    const suggestions = {};

                    let commandBarItems: ICommandBarItemProps[] = [];

                    if (!FormIsDirty || FormSaveStatus === SaveStatus.Pending) {
                        commandBarItems.push({
                            key: 'Close',
                            text: 'Close',
                            iconProps: { iconName: 'Cancel' },
                            onClick: (ev, item) => {
                                props.onCancelled();
                            },
                        });
                    } else {
                        commandBarItems.push(
                            {
                                key: 'Save',
                                text: 'Save',
                                iconProps: { iconName: 'Save' },
                                onClick: (ev, item) => {
                                    saveEntity();
                                },
                            },
                            {
                                key: 'Cancel',
                                text: 'Cancel',
                                iconProps: { iconName: 'Cancel' },
                                onClick: (ev, item) => {
                                    props.onCancelled();
                                },
                            }
                        );
                    }

                    // Row labels: Name / Project ref
                    const nameLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Name</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const projectRefLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Project ref</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    // Row labels: Parent Type / Parent Portfolio or Programme
                    const parentTypeLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Parent Type</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const parentPortfolioLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Parent Portfolio</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const parentProgrammeLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Parent Programme</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    // Row labels: Status / Stage
                    const statusLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const stageLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Stage</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const currentPhaseLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Current Phase</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const locationLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Location</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    // Row labels: Start Date / End Date
                    const startDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Start Date</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const endDateLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>End Date</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    // Row labels: SRO / ARA
                    const sroLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Senior Responsible Owner (SRO)</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const araLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Alternative Report Approver (ARA)</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    // Row labels: PM / RL
                    const pmLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Project Manager (PM)</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const rlLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Reporting Lead (RL)</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const showParentFields = !(props.showEntityDescription1 === true);
                    const showParentPortfolio = showParentFields && project.ParentTypeID === 1;
                    const showParentProgramme = showParentFields && project.ParentTypeID === 2;
                    const showParentPair = showParentPortfolio || showParentProgramme;

                    // Project Financials: derive FY labels/rows from the project's Start Date / End Date
                    const projectStartDate = project.StartDate ? DateService.removeTimezoneOffset(project.StartDate) : null;
                    const projectEndDate = project.EndDate ? DateService.removeTimezoneOffset(project.EndDate) : null;
                    const fyLabels = getFYLabelsForRange(projectStartDate, projectEndDate, fyStartMonth, fyStartDay);
                    const financialRows = chunkIntoPairs(fyLabels);

                    // Read the current value for a given FY label straight out of FormData
                    const getFinancialValue = (fyLabel: string): string => {
                        const existing = project.PPAppProjectFinancials?.find(f => f.FYLabel === fyLabel);
                        if (existing?.FYValue === null || existing?.FYValue === undefined) return '';
                        return existing.FYValue.toLocaleString('en-GB');
                    };

                    // Write a value for a given FY label straight into FormData.PPAppProjectFinancials
                    // Whole numbers only — no decimals, since this is a yearly total.
                    const handleFinancialValueChange = (fyLabel: string, newValue: string): void => {
                        const sanitizedValue = newValue.replace(/[^0-9]/g, '');

                        const parsedValue: number = sanitizedValue === '' ? null : parseInt(sanitizedValue, 10);

                        const current = project.PPAppProjectFinancials ? [...project.PPAppProjectFinancials] : [];
                        const existingIndex = current.findIndex(f => f.FYLabel === fyLabel);
                        if (existingIndex !== -1) {
                            current[existingIndex] = { ...current[existingIndex], FYValue: parsedValue };
                        } else {
                            const newFinancial = new PPAppProjectFinancial(project.ID);
                            newFinancial.FYLabel = fyLabel;
                            newFinancial.FYValue = parsedValue;
                            current.push(newFinancial);
                        }
                        changeJson(current, 'PPAppProjectFinancials');
                    };

                    // Drop any financial rows whose FYLabel is no longer within Start Date -> End Date
                    const pruneFinancialsForRange = (startDate: Date, endDate: Date): void => {
                        const validLabels = new Set(getFYLabelsForRange(startDate, endDate, fyStartMonth, fyStartDay));
                        const current = project.PPAppProjectFinancials || [];
                        const pruned = current.filter(f => validLabels.has(f.FYLabel));
                        if (pruned.length !== current.length) {
                            changeJson(pruned, 'PPAppProjectFinancials');
                        }
                    };

                    const handleStartDateChange = (date: Date): void => {
                        changeDatePicker(date, 'StartDate');
                        pruneFinancialsForRange(date, projectEndDate);
                    };

                    const handleEndDateChange = (date: Date): void => {
                        changeDatePicker(date, 'EndDate');
                        pruneFinancialsForRange(projectStartDate, date);
                    };

                    const renderFinancialLabel = (fyLabel: string) => (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>{fyLabel}</span></div>
                                <div className={styles.sectionQuestionCol2}>
                                </div>
                            </div>
                        </div>
                    );

                    // Total Project Cost: sum of every entered FY value
                    const getTotalProjectCost = (): string => {
                        const total = (project.PPAppProjectFinancials || []).reduce((sum, f) => sum + (f.FYValue || 0), 0);
                        return total.toLocaleString('en-GB');
                    };

                    // Total Benefits / Net Present Value
                    const getFinancialNumberValue = (value: number): string => {
                        if (value === null || value === undefined) return '';
                        return value.toLocaleString('en-GB');
                    };

                    const handleFinancialNumberChange = (fieldName: 'TotalBenefits' | 'NetPresentValue', newValue: string): void => {
                        const sanitizedValue = newValue.replace(/[^0-9]/g, '');
                        const parsedValue: number = sanitizedValue === '' ? null : parseInt(sanitizedValue, 10);

                        changeJson(parsedValue, fieldName);
                    };

                    // Row labels: Current Financial Year / Total Project Cost
                    const currentFYLabelLbl = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Current Financial Year</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const totalProjectCostLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Total Project Cost</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const totalBenefitsLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Total Benefits</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    const netPresentValueLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Net Present Value</span></div>
                            <div className={styles.sectionQuestionCol2}>
                            </div>
                        </div>
                    </div>;

                    return (
                        <div>
                            <CrCommandBar items={commandBarItems} />

                            {/* Basic Details Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '10px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Basic Details</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)' }}>

                                    {/* Name / Project ref */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {nameLabel}
                                            {projectRefLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && nameLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    required={true}
                                                    disabled={externalDataSource || disableEdit || !userPermissions.UserIsSystemAdmin()}
                                                    maxLength={255}
                                                    value={project.Title}
                                                    onChange={(ev, newValue) => changeTextField(newValue, 'Title')}
                                                    errorMessage={errors.Title}
                                                />
                                            </div>
                                            {isMobile && projectRefLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    disabled={externalDataSource || disableProjectManagement}
                                                    maxLength={100}
                                                    labelIcon="Dynamics365Logo"
                                                    placeholder={disableProjectManagement ? "" : "Enter external reference or id if available"}
                                                    value={project.CorporateProjectID}
                                                    onChange={(ev, newValue) => changeTextField(newValue, 'CorporateProjectID')}
                                                    errorMessage={errors.CorporateProjectID}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Parent Type / Parent Portfolio or Programme */}
                                    {showParentFields && showParentPair &&
                                        <div className={styles.formField}>
                                            {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                {parentTypeLabel}
                                                {showParentPortfolio ? parentPortfolioLabel : parentProgrammeLabel}
                                            </div>}
                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && parentTypeLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        required={true}
                                                        options={LookupService.entitiesToSelectableOptions([
                                                            { ID: 1, Title: 'Portfolio' },
                                                            { ID: 2, Title: 'Programme' },
                                                        ])}
                                                        selectedKey={project.ParentTypeID}
                                                        onChange={(_, v) => changeDropdown(v, 'ParentTypeID')}
                                                        errorMessage={errors.ParentType}
                                                    />
                                                </div>
                                                {isMobile && (showParentPortfolio ? parentPortfolioLabel : parentProgrammeLabel)}
                                                <div className='two-fields-sidebyside-field'>
                                                    {showParentPortfolio &&
                                                        <CrDropdown
                                                            required={true}
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Portfolios.filter(x => x.ContainsValue === 'Projects'))}
                                                            selectedKey={project.PortfolioID}
                                                            onChange={(_, v) => changeDropdown(v, 'PortfolioID')}
                                                            errorMessage={errors.PortfolioID}
                                                        />
                                                    }
                                                    {showParentProgramme &&
                                                        <CrDropdown
                                                            required={true}
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Programmes)}
                                                            selectedKey={project.ProgrammeID}
                                                            onChange={(_, v) => changeDropdown(v, 'ProgrammeID')}
                                                            errorMessage={errors.ProgrammeID}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    {showParentFields && !showParentPair &&
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Parent Type</span></div>
                                                        <div className={styles.sectionQuestionCol2}>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDropdown
                                                        required={true}
                                                        options={LookupService.entitiesToSelectableOptions([
                                                            { ID: 1, Title: 'Portfolio' },
                                                            { ID: 2, Title: 'Programme' },
                                                        ])}
                                                        selectedKey={project.ParentTypeID}
                                                        onChange={(_, v) => changeDropdown(v, 'ParentTypeID')}
                                                        errorMessage={errors.ParentType}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    }

                                    {/* Status / Stage */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {statusLabel}
                                            {stageLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && statusLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    required={true}
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                                    selectedKey={project.EntityStatusID}
                                                    onChange={(_, v) => changeStatusDropdown(v, 'EntityStatusID')}
                                                    errorMessage={errors.EntityStatusID}
                                                />
                                            </div>
                                            {isMobile && stageLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    required={true}
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.ProjectStages)}
                                                    selectedKey={project.ProjectStageID}
                                                    onChange={(_, v) => changeDropdown(v, 'ProjectStageID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Current Phase / Location */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {currentPhaseLabel}
                                            {locationLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && currentPhaseLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.ProjectPhases)}
                                                    selectedKey={project.ProjectPhaseID}
                                                    onChange={(_, o) => changeDropdown(o, 'ProjectPhaseID')}
                                                />
                                            </div>
                                            {isMobile && locationLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.ZCoreLocations)}
                                                    selectedKey={project.ProjectLocationID}
                                                    onChange={(_, o) => changeDropdown(o, 'ProjectLocationID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Project Objective */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Project Objective</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    multiline
                                                    rows={6}
                                                    maxLength={10000}
                                                    value={project.Objectives}
                                                    onChange={(ev, newValue) => changeTextField(newValue, 'Objectives')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Start Date / End Date */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {startDateLabel}
                                            {endDateLabel}
                                        </div>}

                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && startDateLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    disabled={disableEdit}
                                                    value={DateService.removeTimezoneOffset(project.StartDate)}
                                                    onSelectDate={date => handleStartDateChange(date)}
                                                    required={true}
                                                    maxWidth='100%'
                                                    errorMessage={errors.StartDate}
                                                />
                                            </div>

                                            {isMobile && endDateLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    disabled={disableEdit}
                                                    value={DateService.removeTimezoneOffset(project.EndDate)}
                                                    minDate={DateService.removeTimezoneOffset(project.StartDate)}
                                                    onSelectDate={date => handleEndDateChange(date)}
                                                    required={true}
                                                    maxWidth='100%'
                                                    errorMessage={errors.EndDate}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Project Financials Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Project Financials</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)' }}>
                                    {financialRows.length === 0 &&
                                        <div style={{
                                            padding: '10px 12px',
                                            marginBottom: '15px',
                                            backgroundColor: '#f3f2f1',
                                            borderLeft: '3px solid #0078d4',
                                            color: '#605e5c',
                                            fontSize: '13px'
                                        }}>
                                            Set the Start Date and End Date above to enter project costs by financial year.
                                        </div>
                                    }
                                    {financialRows.map((row, idx) => {
                                        const firstLabel = row[0];
                                        const secondLabel = row.length > 1 ? row[1] : null;
                                        return (
                                            <div className={styles.formField} key={`financial-row-${idx}`}>
                                                {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                    {renderFinancialLabel(firstLabel)}
                                                    {secondLabel && renderFinancialLabel(secondLabel)}
                                                </div>}
                                                <div className='two-fields-sidebyside-r2'>
                                                    {isMobile && renderFinancialLabel(firstLabel)}
                                                    <div className='two-fields-sidebyside-field'>
                                                        <CrTextField
                                                            value={getFinancialValue(firstLabel)}
                                                            onChange={(ev, newValue) => handleFinancialValueChange(firstLabel, newValue)}
                                                        />
                                                    </div>
                                                    {secondLabel && isMobile && renderFinancialLabel(secondLabel)}
                                                    {secondLabel &&
                                                        <div className='two-fields-sidebyside-field'>
                                                            <CrTextField
                                                                value={getFinancialValue(secondLabel)}
                                                                onChange={(ev, newValue) => handleFinancialValueChange(secondLabel, newValue)}
                                                            />
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Current Financial Year / Total Project Cost (readonly, calculated) */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {currentFYLabelLbl}
                                            {totalProjectCostLabel}
                                        </div>}

                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && currentFYLabelLbl}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    value={`${DateService.dateToUkDate(currentFYStart)} - ${DateService.dateToUkDate(currentFYEnd)}`}
                                                    readonly={true}
                                                    styles={{
                                                        field: {
                                                            backgroundColor: 'rgb(245, 245, 245)'
                                                        }
                                                    }}
                                                />
                                            </div>

                                            {isMobile && totalProjectCostLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    value={getTotalProjectCost()}
                                                    readonly={true}
                                                    styles={{
                                                        field: {
                                                            backgroundColor: 'rgb(245, 245, 245)'
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Total Benefits / Net Present Value */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {totalBenefitsLabel}
                                            {netPresentValueLabel}
                                        </div>}

                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && totalBenefitsLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    value={getFinancialNumberValue(project.TotalBenefits)}
                                                    onChange={(ev, newValue) => handleFinancialNumberChange('TotalBenefits', newValue)}
                                                />
                                            </div>

                                            {isMobile && netPresentValueLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    value={getFinancialNumberValue(project.NetPresentValue)}
                                                    onChange={(ev, newValue) => handleFinancialNumberChange('NetPresentValue', newValue)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ownership and Roles Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Ownership and Roles</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)' }}>

                                    {/* Owner Type */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Owner Type</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions([
                                                        { ID: 0, Title: orbConfig.OrgTypeLabel },
                                                        { ID: 1, Title: orbConfig.OrgL1Label },
                                                        { ID: 2, Title: orbConfig.OrgL2Label },
                                                        { ID: 3, Title: orbConfig.OrgL3Label },
                                                    ])}
                                                    selectedKey={project.OwnerTypeID}
                                                    onChange={(_, o) => changeDropdown(o, 'OwnerTypeID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Owner L1/L2/L3 */}
                                    {project.OwnerTypeID > 0 && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}>
                                                        {project.OwnerTypeID === 1 && <span>{`Owner ${orbConfig.OrgL1Label}`}</span>}
                                                        {project.OwnerTypeID === 2 && <span>{`Owner ${orbConfig.OrgL2Label}`}</span>}
                                                        {project.OwnerTypeID === 3 && <span>{`Owner ${orbConfig.OrgL3Label}`}</span>}
                                                    </div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                {project.OwnerTypeID === 1 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                                                        selectedKey={project.OwnerL1ID}
                                                        onChange={(_, option, index) => changeDropdown(option, 'OwnerL1ID', index)}
                                                        errorMessage={errors.OwnerL1}
                                                    />
                                                }
                                                {project.OwnerTypeID === 2 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                        selectedKey={project.OwnerL2ID}
                                                        onChange={(_, option, index) => changeDropdown(option, 'OwnerL2ID', index)}
                                                        errorMessage={errors.OwnerL2}
                                                    />
                                                }
                                                {project.OwnerTypeID === 3 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                        selectedKey={project.OwnerL3ID}
                                                        onChange={(_, option, index) => changeDropdown(option, 'OwnerL3ID', index)}
                                                        errorMessage={errors.OwnerL3}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    </div>}

                                    {/* Managed By Type */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Managed By Type</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions([
                                                        { ID: 2, Title: orbConfig.OrgL2Label },
                                                        { ID: 3, Title: orbConfig.OrgL3Label },
                                                    ])}
                                                    selectedKey={project.ManagedByTypeID}
                                                    onChange={(_, o) => changeDropdown(o, 'ManagedByTypeID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Managed By L2/L3 */}
                                    {project.ManagedByTypeID > 1 && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}>
                                                        {project.ManagedByTypeID === 2 && <span>{`Managed By ${orbConfig.OrgL2Label}`}</span>}
                                                        {project.ManagedByTypeID === 3 && <span>{`Managed By ${orbConfig.OrgL3Label}`}</span>}
                                                    </div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                {project.ManagedByTypeID === 2 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                        selectedKey={project.ManagedByL2ID}
                                                        onChange={(_, option, index) => changeDropdown(option, 'ManagedByL2ID', index)}
                                                        errorMessage={errors.ManagedByL2}
                                                    />
                                                }
                                                {project.ManagedByTypeID === 3 &&
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                        selectedKey={project.ManagedByL3ID}
                                                        onChange={(_, option, index) => changeDropdown(option, 'ManagedByL3ID', index)}
                                                        errorMessage={errors.ManagedByL3}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    </div>}

                                    {/* SRO / ARA */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {sroLabel}
                                            {araLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && sroLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrUserPicker
                                                    disabled={disableEdit}
                                                    users={users}
                                                    selectedUsers={project.SeniorResponsibleOwnerUserID && [project.SeniorResponsibleOwnerUserID]}
                                                    onChange={v => changeUserPicker(v, 'SeniorResponsibleOwnerUserID')}
                                                    {...suggestions}
                                                />
                                            </div>
                                            {isMobile && araLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrUserPicker
                                                    users={users}
                                                    selectedUsers={project.ReportApproverUserID && [project.ReportApproverUserID]}
                                                    onChange={v => changeUserPicker(v, 'ReportApproverUserID')}
                                                    {...suggestions}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* PM / RL */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {pmLabel}
                                            {rlLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && pmLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrUserPicker
                                                    users={users}
                                                    selectedUsers={project.ProjectManagerUserID && [project.ProjectManagerUserID]}
                                                    onChange={v => changeUserPicker(v, 'ProjectManagerUserID')}
                                                    {...suggestions}
                                                />
                                            </div>
                                            {isMobile && rlLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrUserPicker
                                                    users={users}
                                                    selectedUsers={project.ReportingLeadUserID && [project.ReportingLeadUserID]}
                                                    onChange={u => changeUserPicker(u, 'ReportingLeadUserID')}
                                                    {...suggestions}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Headline update contributors (HUC) */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Headline Update Contributors (HUC)</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrUserPicker
                                                    users={users}
                                                    itemLimit={3}
                                                    selectedUsers={project.Contributors?.map(c => c.ContributorUserID)}
                                                    onChange={v => changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                                                    {...suggestions}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Additional Information Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Additional Information</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)' }}>

                                    {/* Dependent On */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Dependent On</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    multiSelect
                                                    options={LookupService.entitiesToSelectableOptions(filteredProjectsForDepList)}
                                                    selectedKeys={project.ProjectDependencies?.map(x => x.DependentProjectID)}
                                                    onChange={(_, v) =>
                                                        changeMultiDropdown(
                                                            v,
                                                            'ProjectDependencies',
                                                            new ProjectDep(),
                                                            'DependentProjectID'
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reports due */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Reports Due</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrReportingCyclePicker
                                                    required={true}
                                                    cycle={{ frequency: project.ReportingFrequency, dueDay: project.ReportingDueDay, startDate: project.ReportingStartDate }}
                                                    onChange={cycle => changeReportingCycle(cycle)}
                                                    errorMessage={errors.ReportingCycle}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Information (custom fields) */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Additional Information</span></div>
                                                    <div className={styles.sectionQuestionCol2}>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrCustomFields
                                                    customFieldDfns={filteredCustomFieldDfns}
                                                    selectedItems={LookupService.customFieldsToDropdownWithText(project.CustomFields)}
                                                    onChange={v => changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Integration Details Section */}
                            {disableProjectManagement && project.CorporateProjectID !== orbOnlyProjectFlag &&
                                <div>
                                    <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className={styles.sectionATitle}>Integration Details</div>
                                        </div>
                                    </div>

                                    <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '10px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)' }}>
                                        <div className={styles.formField}>
                                            <CrLabel>{dataSourceName || 'Integration'} ID</CrLabel>
                                            <p className={styles.formText}>{project.IntegrationID}</p>
                                        </div>
                                        <div className={styles.formField}>
                                            <CrLabel>{dataSourceName || 'Integration'} last modified</CrLabel>
                                            <p className={styles.formText}>{DateService.dateToUkDateTime(project.IntegrationLastModified)}</p>
                                        </div>
                                    </div>
                                </div>
                            }

                            {/* Discussion, General Comments and File Attachments Section */}
                            {props.entityId > 0 &&
                                <div>
                                    <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className={styles.sectionATitle}>Discussion, General Comments and File Attachments</div>
                                        </div>
                                    </div>
                                    <div style={{ minHeight: '120px' }}>
                                        <EvidenceList
                                            evidenceFor='Project'
                                            projectID={props.entityId}
                                            currentUserId={currentUserId}
                                            superUserPermission={userPermissions.UserIsSystemAdmin()}
                                            isViewOnly={false}
                                            filterText={evidence_ListFilterText}
                                            onChangeFilterText={handleEvidence_ChangeFilterText}
                                            evChangesCounter={1}
                                            {...props}
                                            errorHandling={props.errorHandling}
                                        />
                                    </div>
                                </div>
                            }
                        </div>
                    );
                }}
                loadEntity={projectId => projectService.read(projectId, true, true)}
                loadNewEntity={() => {
                    const proj = new Project();
                    if (disableProjectManagement) proj.CorporateProjectID = orbOnlyProjectFlag;
                    return proj;
                }}
                loadEntityValidations={() => new ProjectValidations()}
                onValidateEntity={validateEntity}
                onCreate={p => projectService.create(p)}
                onUpdate={p => projectService.update(p.ID, p)}
                parentEntities={projectService.parentEntities}
                onAfterUpdate2={async (e) => {
                    const updatedEntity = {
                        ...e,
                        InternalMsg: "FromPPPSyncData"
                    };

                    await projectService.updatePut(updatedEntity.ID, updatedEntity);
                    return updatedEntity;

                }}
                childEntities={[
                    {
                        ObjectParentProperty: 'Contributors',
                        ParentIdProperty: 'ProjectID',
                        ChildIdProperty: 'ContributorUserID',
                        ChildService: contributorService
                    },
                    {
                        ObjectParentProperty: 'CustomFields',
                        ParentIdProperty: 'ProjectID',
                        ChildIdProperty: 'CustomFieldDefnID',
                        ChildService: customFieldService
                    },
                    {
                        ObjectParentProperty: 'ProjectDependencies',
                        ParentIdProperty: 'ProjectID',
                        ChildIdProperty: 'DependentProjectID',
                        ChildService: projectDepService
                    },
                    {
                        ObjectParentProperty: 'PPAppProjectFinancials',
                        ParentIdProperty: 'ProjectID',
                        ChildIdProperty: 'FYLabel',
                        ChildService: ppAppProjectFinancialService
                    }

                ]}
            />
        </div>
    );
};